import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Button,
    TextField,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    '& .MuiIconButton-root': {
        marginRight: theme.spacing(1),
    }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(2),
    minHeight: '300px',
    minWidth: '300px',
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    marginBottom: theme.spacing(0.5),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(2),
    float: 'right',
}));
const CategoryIcon = styled('img')({
    width: '40px',
    height: '40px',
    objectFit: 'contain',
    marginRight: '15px',
    flexShrink: 0,
});


const CategorySelector = ({
    open,
    handleClose,
    categories,
    handleCategorySelect,
    handleCategoryBack,
    hasBackButton
}) => {
    // Fallback icon now points to the local X.png in the icons folder
    const [searchTerm, setSearchTerm] = useState('');
    const fallbackIcon = `${process.env.PUBLIC_URL}/icons/X.png`;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    maxWidth: '600px',
                    width: '100%',
                }
            }}
        >
            <StyledDialogTitle>
                {hasBackButton && (
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleCategoryBack}
                        aria-label="back"
                        size="small"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                )}
                Select category
            </StyledDialogTitle>
            <StyledDialogContent>
                <TextField
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search categories"
                    margin="dense"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <List>
                    {categories
                        .filter((cat) =>
                            cat.name.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map((category) => (
                        <StyledListItem
                            button
                            key={category.id}
                            onClick={() => handleCategorySelect(category)}
                        >
                            <CategoryIcon
                                src={`${process.env.PUBLIC_URL}/icons/${category.id}.png`}
                                alt={category.name}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = fallbackIcon;
                                }}
                            />

                            <ListItemText
                                primary={category.name}
                                primaryTypographyProps={{
                                    sx: { fontWeight: 'normal' }
                                }}
                            />
                        </StyledListItem>
                    ))}
                </List>
                <StyledButton
                    onClick={handleClose}
                    variant="outlined"
                    color="primary"
                >
                    Cancel
                </StyledButton>
            </StyledDialogContent>
        </Dialog>
    );
};

CategorySelector.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    handleCategorySelect: PropTypes.func.isRequired,
    handleCategoryBack: PropTypes.func.isRequired,
    hasBackButton: PropTypes.bool.isRequired,
};

export default CategorySelector;
